import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { getDoc, doc, setDoc, updateDoc } from "firebase/firestore";
import { db } from '../firebase-config';
import { AppCTX } from '../data/AppContext';
import { defaultUserModel } from '../data/Models';

const SUPERUSER_UID = process.env.REACT_APP_SUPERUSER;
const TEAMNAME = process.env.REACT_APP_TEAMNAME || 'DeltaPower';

export default function TeamSignUp() {
  const { user, setUser } = React.useContext(AppCTX);
  const [userExists, setUserExists] = useState(null); // null = loading, true/false = checked
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [inviteData, setInviteData] = useState(null); // Holds code info after verification
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function checkUser() {
      if (!user.uid) return;
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      setUserExists(userSnap.exists());
    }
    checkUser();
  }, [user.uid]);

  // Superuser creation logic
  const handleSuperuserCreate = async () => {
    if (!name) {
      setError('Please enter your name');
      return;
    }
    const color = '#000000';
    const newUser = {
      ...defaultUserModel,
      name,
      color,
      teamName: TEAMNAME,
      memberType: 'superuser',
    };
    await setDoc(doc(db, 'users', user.uid), newUser);
    setUser({ uid: user.uid, data: newUser });
  };

  // 1. User enters code, 2. Verify code
  const handleVerifyCode = async () => {
    setCodeError('');
    setLoading(true);
    try {
      const codeRef = doc(db, 'invitationCodes', code);
      const codeSnap = await getDoc(codeRef);
      if (!codeSnap.exists()) {
        setCodeError('Invalid code.');
        setLoading(false);
        return;
      }
      const data = codeSnap.data();
      if (!data.isActive || data.usedBy) {
        setCodeError('Code has already been used.');
        setLoading(false);
        return;
      }
      setInviteData(data);
    } catch (err) {
      setCodeError('Error verifying code.');
    }
    setLoading(false);
  };

  // 3. User enters name, 4. Create user
  const handleNormalUserCreate = async () => {
    setNameError('');
    if (!name) {
      setNameError('Please enter your name');
      return;
    }
    setLoading(true);
    try {
      const color = `#${Math.floor(Math.random()*16777215).toString(16)}`;
      const newUser = {
        ...defaultUserModel,
        name,
        color,
        teamName: inviteData.teamName,
        memberType: inviteData.memberType,
      };
      await setDoc(doc(db, 'users', user.uid), newUser);
      // Add user to team members array
      const teamRef = doc(db, 'teams', inviteData.teamName);
      const teamSnap = await getDoc(teamRef);
      if (teamSnap.exists()) {
        const teamData = teamSnap.data();
        const updatedMembers = Array.isArray(teamData.members) ? [...teamData.members, newUser] : [newUser];
        await setDoc(teamRef, { ...teamData, members: updatedMembers });
      } else {
        // If team doc doesn't exist, create it (shouldn't happen in normal flow)
        await setDoc(teamRef, { members: [newUser], areas: {}, teamLead: '' });
      }
      // Mark code as used
      await updateDoc(doc(db, 'invitationCodes', code), {
        usedBy: user.uid,
        usedAt: new Date(),
        isActive: false,
      });
      setUser({ uid: user.uid, data: newUser });
    } catch (err) {
      setNameError('Error creating user.');
    }
    setLoading(false);
  };

  if (userExists === null) return null;
  if (userExists) return null;

  // Superuser flow
  if (user.uid === SUPERUSER_UID) {
    return (
      <Box sx={{ minHeight: '100vh', width: '100vw', background: '#111111', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ width: '90%', maxWidth: 400, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.15)', p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <img src={process.env.PUBLIC_URL + '/closed.png'} alt="Delta Power Group Logo" style={{ width: '180px', height: 'auto', marginBottom: 24, marginTop: 8 }} />
          <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>Superuser Setup</Typography>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Your Name"
            value={name}
            onChange={e => setName(e.target.value)}
            InputLabelProps={{ style: { color: '#bbb' } }}
            InputProps={{ style: { color: '#fff', background: '#222', borderRadius: 8 } }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2, bgcolor: '#fff', color: '#181818', '&:hover': { bgcolor: '#e0e0e0' } }}
            onClick={handleSuperuserCreate}
            disabled={loading}
          >
            Create My Account
          </Button>
          {error && <Typography sx={{ color: 'red', mt: 1 }}>{error}</Typography>}
        </Box>
      </Box>
    );
  }

  // Normal user flow
  return (
    <Box sx={{ minHeight: '100vh', width: '100vw', background: '#111111', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ width: '90%', maxWidth: 400, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.15)', p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <img src={process.env.PUBLIC_URL + '/logo.jpeg'} alt="Delta Power Group Logo" style={{ width: '180px', height: 'auto', marginBottom: 24, marginTop: 8 }} />
        {!inviteData ? (
          <>
            <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>Enter Invitation Code</Typography>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Invitation Code"
              value={code}
              onChange={e => setCode(e.target.value)}
              InputLabelProps={{ style: { color: '#bbb' } }}
              InputProps={{ style: { color: '#fff', background: '#222', borderRadius: 8 } }}
              error={!!codeError}
              helperText={codeError}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, bgcolor: '#fff', color: '#181818', '&:hover': { bgcolor: '#e0e0e0' } }}
              onClick={handleVerifyCode}
              disabled={loading || !code}
            >
              Verify Code
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>Enter Your Name</Typography>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Your Name"
              value={name}
              onChange={e => setName(e.target.value)}
              InputLabelProps={{ style: { color: '#bbb' } }}
              InputProps={{ style: { color: '#fff', background: '#222', borderRadius: 8 } }}
              error={!!nameError}
              helperText={nameError}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, bgcolor: '#fff', color: '#181818', '&:hover': { bgcolor: '#e0e0e0' } }}
              onClick={handleNormalUserCreate}
              disabled={loading || !name}
            >
              Create My Account
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
